<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import {
  getWhatsAppTemplateKey,
  useWhatsAppTemplatePicker,
} from 'dashboard/composables/useWhatsAppTemplatePicker';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const teams = useMapGetter('teams/getTeams');
const uiFlags = useMapGetter('inboxes/getUIFlags');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const templateTeamAssignments = ref({});
const isSaving = ref(false);

const { processTemplates } = useWhatsAppTemplatePicker(() => props.inbox.id);

const templates = computed(() =>
  processTemplates(getFilteredWhatsAppTemplates.value(props.inbox.id))
);

const isIungoChannel = computed(
  () => props.inbox.channel_type === 'Channel::Iungo'
);

const loadAssignments = () => {
  templateTeamAssignments.value = { ...(props.inbox.template_teams || {}) };
};

const getAssignedTeams = template => {
  const templateKey = getWhatsAppTemplateKey(template);
  return templateTeamAssignments.value[templateKey] || [];
};

const toggleTeamForTemplate = (template, teamId) => {
  const templateKey = getWhatsAppTemplateKey(template);
  const currentTeams = [...(templateTeamAssignments.value[templateKey] || [])];
  const normalizedTeamId = Number(teamId);
  const teamIndex = currentTeams.indexOf(normalizedTeamId);

  if (teamIndex >= 0) {
    currentTeams.splice(teamIndex, 1);
  } else {
    currentTeams.push(normalizedTeamId);
  }

  templateTeamAssignments.value = {
    ...templateTeamAssignments.value,
    [templateKey]: currentTeams,
  };
};

const isTeamAssigned = (template, teamId) => {
  return getAssignedTeams(template).includes(Number(teamId));
};

const saveAssignments = async () => {
  isSaving.value = true;
  try {
    const payload = {
      id: props.inbox.id,
      formData: false,
      channel: isIungoChannel.value
        ? {
            additional_attributes: {
              ...(props.inbox.additional_attributes || {}),
              template_teams: templateTeamAssignments.value,
            },
          }
        : {
            provider_config: {
              ...(props.inbox.provider_config || {}),
              template_teams: templateTeamAssignments.value,
            },
          },
    };

    await store.dispatch('inboxes/updateInbox', payload);
    useAlert(t('WHATSAPP_TEMPLATES.ORGANIZATION.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('WHATSAPP_TEMPLATES.ORGANIZATION.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

watch(() => props.inbox, loadAssignments, { immediate: true, deep: true });

onMounted(() => {
  store.dispatch('teams/get');
});
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="t('WHATSAPP_TEMPLATES.ORGANIZATION.TITLE')"
      :sub-title="t('WHATSAPP_TEMPLATES.ORGANIZATION.SUBTITLE')"
    >
      <p class="mb-4 text-sm text-n-slate-11">
        {{ t('WHATSAPP_TEMPLATES.ORGANIZATION.HELP_TEXT') }}
      </p>

      <div
        v-if="!templates.length"
        class="py-6 text-sm text-center text-n-slate-11"
      >
        {{ t('WHATSAPP_TEMPLATES.ORGANIZATION.NO_TEMPLATES') }}
      </div>

      <div v-else class="flex flex-col gap-4">
        <div
          v-for="template in templates"
          :key="getWhatsAppTemplateKey(template)"
          class="p-4 rounded-lg outline outline-1 outline-n-weak"
        >
          <div class="flex justify-between items-start gap-4 mb-3">
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ template.name }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ template.language }} · {{ template.category }}
              </p>
            </div>
            <span
              v-if="!getAssignedTeams(template).length"
              class="px-2 py-1 text-xs rounded-lg bg-n-slate-3 text-n-slate-11"
            >
              {{ t('WHATSAPP_TEMPLATES.ORGANIZATION.ALL_TEAMS') }}
            </span>
          </div>

          <div class="flex flex-wrap gap-2">
            <button
              v-for="team in teams"
              :key="`${template.name}-${team.id}`"
              class="px-2.5 py-1 text-xs rounded-lg outline outline-1 transition-colors"
              :class="
                isTeamAssigned(template, team.id)
                  ? 'bg-n-brand/10 outline-n-brand text-n-brand'
                  : 'bg-n-alpha-black2 outline-n-weak text-n-slate-11'
              "
              @click="toggleTeamForTemplate(template, team.id)"
            >
              {{ team.name }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="templates.length" class="flex justify-end mt-6">
        <NextButton
          :label="t('WHATSAPP_TEMPLATES.ORGANIZATION.SAVE_BUTTON')"
          :is-loading="isSaving || uiFlags.isUpdating"
          @click="saveAssignments"
        />
      </div>
    </SettingsSection>
  </div>
</template>
